import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import {
  Building2, FileText, CreditCard, Receipt, PenTool, Save, Loader2, Plus, Pencil, Trash2,
  ImagePlus, X, Star, CheckCircle2,
} from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import api from "@/lib_app/api";
import Avatar, { fileToAvatarDataURL } from "@/components/app/Avatar";

const NAV = [
  { key: "identity", label: "Identitas Perusahaan", icon: Building2, desc: "Logo, nama resmi, alamat & kontak" },
  { key: "documents", label: "Nomor & Dokumen", icon: FileText, desc: "Format penomoran & jatuh tempo" },
  { key: "banks", label: "Rekening Bank", icon: CreditCard, desc: "Rekening PT / Pribadi untuk invoice" },
  { key: "tax", label: "Pajak & NPWP", icon: Receipt, desc: "NPWP PT & data pribadi Non-PPN" },
  { key: "signers", label: "Penandatangan Invoice", icon: PenTool, desc: "Siapa yang tanda tangan invoice" },
];

const label = "text-[10px] font-bold text-[#3B4A63] uppercase tracking-[0.12em] mb-1.5 block";
const cardCls = "bg-white rounded-2xl border border-[#DCE0E7] card-soft p-6 md:p-7";

export default function CompanySettings() {
  const [nav, setNav] = useState("identity");
  const [company, setCompany] = useState(null);

  const loadCompany = () => api.get("/settings/company").then((r) => setCompany(r.data)).catch(() => {});
  useEffect(() => { loadCompany(); }, []);

  const saveCompany = async (patch) => {
    const res = await api.patch("/settings/company", { ...company, ...patch });
    setCompany(res.data);
    toast.success("Pengaturan disimpan");
  };

  const active = NAV.find((n) => n.key === nav);

  return (
    <div className="grid grid-cols-1 lg:grid-cols-[264px_minmax(0,1fr)] gap-5" data-testid="company-settings">
      {/* SUB NAV */}
      <nav className="flex lg:flex-col gap-1.5 overflow-x-auto lg:overflow-visible pb-1" data-testid="company-settings-nav">
        {NAV.map((n) => {
          const Ico = n.icon;
          const on = nav === n.key;
          return (
            <button
              key={n.key}
              onClick={() => setNav(n.key)}
              data-testid={`company-nav-${n.key}`}
              className={`text-left rounded-2xl px-4 py-3 border transition-colors shrink-0 lg:shrink ${
                on
                  ? "bg-[#090808] border-[#090808] text-white shadow-[0_8px_20px_rgba(9,8,8,0.18)]"
                  : "bg-white border-[#DCE0E7] text-[#626061] hover:border-[#3B4A63] hover:bg-[#EDF0F4]/60"
              }`}
            >
              <div className="flex items-center gap-2">
                <Ico className={`w-4 h-4 shrink-0 ${on ? "text-white" : "text-[#3B4A63]"}`} />
                <span className={`text-[13px] font-bold whitespace-nowrap ${on ? "text-white" : "text-[#090808]"}`}>{n.label}</span>
              </div>
              <div className={`text-[11px] mt-0.5 hidden lg:block ${on ? "text-white/65" : "text-[#A0A3A8]"}`}>{n.desc}</div>
            </button>
          );
        })}
      </nav>

      <div className="min-w-0 space-y-4">
        <div>
          <h2 className="text-lg font-bold text-[#090808] tracking-tight">{active?.label}</h2>
          <p className="text-xs text-[#626061] mt-0.5">{active?.desc}</p>
        </div>

        {!company && nav !== "banks" && nav !== "signers" ? (
          <div className={cardCls}><Loader2 className="w-4 h-4 animate-spin text-[#A0A3A8]" /></div>
        ) : (
          <>
            {nav === "identity" && <IdentitySection company={company} onSave={saveCompany} />}
            {nav === "documents" && <DocumentsSection company={company} onSave={saveCompany} />}
            {nav === "banks" && <BankAccountsSection />}
            {nav === "tax" && <TaxSection company={company} onSave={saveCompany} />}
            {nav === "signers" && <SignersSection />}
          </>
        )}
      </div>
    </div>
  );
}

/* ---------------- shared bits ---------------- */

function SaveBar({ dirty, busy, onSave, onReset, testid }) {
  return (
    <div className="flex items-center justify-end gap-2 pt-5 mt-5 border-t border-[#DCE0E7]">
      {dirty && (
        <button onClick={onReset} data-testid={`${testid}-reset`} className="h-10 px-4 rounded-full text-sm font-semibold text-[#626061] hover:bg-[#F4F5F7]">
          Batalkan
        </button>
      )}
      <Button
        onClick={onSave} disabled={!dirty || busy} data-testid={`${testid}-save`}
        className="h-10 rounded-full bg-[#090808] hover:bg-black gap-1.5 px-5 disabled:opacity-40"
      >
        {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
        {busy ? "Menyimpan…" : dirty ? "Simpan Perubahan" : "Tersimpan"}
      </Button>
    </div>
  );
}

function useDraft(source, keys) {
  const pick = (o) => keys.reduce((a, k) => ({ ...a, [k]: o?.[k] ?? "" }), {});
  const [draft, setDraft] = useState(() => pick(source));
  useEffect(() => { setDraft(pick(source)); /* eslint-disable-next-line */ }, [source]);
  const dirty = keys.some((k) => String(draft[k] ?? "") !== String(source?.[k] ?? ""));
  const set = (k, v) => setDraft((d) => ({ ...d, [k]: v }));
  return { draft, set, dirty, reset: () => setDraft(pick(source)) };
}

function Row({ children, cols = 2 }) {
  return <div className={`grid grid-cols-1 md:grid-cols-${cols} gap-4`}>{children}</div>;
}

function Badge({ tone = "blue", children, testid }) {
  const tones = {
    blue: "bg-[#E5EEFA] text-[#3B4A63] border-[#B0C7EA]",
    gray: "bg-[#F4F5F7] text-[#626061] border-[#DCE0E7]",
    green: "bg-[#C9F0D6] text-[#1E6B39] border-[#B7E7C7]",
    purple: "bg-[#F3E8FF] text-[#6B21A8] border-[#E4CCFB]",
  };
  return (
    <span data-testid={testid} className={`inline-flex items-center gap-1 h-6 px-2.5 rounded-full border text-[10px] font-bold uppercase tracking-wide ${tones[tone]}`}>
      {children}
    </span>
  );
}

function EmptyState({ icon: Icon, title, hint, cta, onCta, testid }) {
  return (
    <div className="rounded-2xl border border-dashed border-[#DCE0E7] bg-[#FAFBFD] py-12 px-6 text-center" data-testid={testid}>
      <div className="w-12 h-12 mx-auto rounded-2xl bg-white border border-[#DCE0E7] flex items-center justify-center text-[#3B4A63]">
        <Icon className="w-5 h-5" />
      </div>
      <div className="text-sm font-bold text-[#090808] mt-3">{title}</div>
      <div className="text-xs text-[#A0A3A8] mt-1 max-w-sm mx-auto">{hint}</div>
      <Button onClick={onCta} data-testid={`${testid}-cta`} className="mt-4 h-10 rounded-full bg-[#090808] hover:bg-black gap-1.5 px-5">
        <Plus className="w-4 h-4" /> {cta}
      </Button>
    </div>
  );
}

/* ---------------- 1. Identitas ---------------- */

function IdentitySection({ company, onSave }) {
  const keys = ["name", "address", "email", "phone", "web", "logo"];
  const { draft, set, dirty, reset } = useDraft(company, keys);
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);

  const onFile = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    if (!f.type.startsWith("image/")) return toast.error("File harus gambar");
    try { set("logo", await fileToAvatarDataURL(f)); } catch { toast.error("Gagal memproses gambar"); }
  };

  const save = async () => {
    setBusy(true);
    try { await onSave(keys.reduce((a, k) => ({ ...a, [k]: draft[k] || "" }), {})); }
    catch (err) { toast.error(err.response?.data?.detail || "Gagal menyimpan"); }
    finally { setBusy(false); }
  };

  return (
    <div className={cardCls} data-testid="company-identity-section">
      <div className="flex flex-col sm:flex-row gap-6">
        <div className="shrink-0">
          <span className={label}>Logo</span>
          <button
            onClick={() => fileRef.current?.click()}
            data-testid="company-logo-picker"
            className="w-[120px] h-[120px] rounded-2xl border-2 border-dashed border-[#DCE0E7] hover:border-[#3B4A63] bg-[#F4F5F7] flex flex-col items-center justify-center gap-1.5 text-[#A0A3A8] hover:text-[#3B4A63] transition-colors overflow-hidden"
          >
            {draft.logo ? (
              <img src={draft.logo} alt="Logo" className="w-full h-full object-contain bg-white" />
            ) : (
              <>
                <ImagePlus className="w-5 h-5" />
                <span className="text-[10px] font-semibold">Unggah logo</span>
              </>
            )}
          </button>
          {draft.logo && (
            <button onClick={() => set("logo", "")} data-testid="company-logo-remove" className="mt-2 inline-flex items-center gap-1 text-[11px] text-[#A0A3A8] hover:text-[#CE6968]">
              <X className="w-3 h-3" /> Hapus logo
            </button>
          )}
          <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={onFile} data-testid="company-logo-input" />
        </div>

        <div className="flex-1 min-w-0 space-y-4">
          <div>
            <Label className={label}>Nama Perusahaan</Label>
            <Input data-testid="company-name-input" value={draft.name} onChange={(e) => set("name", e.target.value)} placeholder="PT ABC Sejahtera" className="rounded-xl" />
          </div>
          <div>
            <Label className={label}>Alamat</Label>
            <textarea
              data-testid="company-address-input" value={draft.address} onChange={(e) => set("address", e.target.value)}
              rows={3} maxLength={500} placeholder="Jl. Contoh No. 123, Jakarta 12345"
              className="w-full rounded-xl border border-[#DCE0E7] bg-white px-3 py-2 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-black/10"
            />
            <div className="text-[10px] text-[#A0A3A8] text-right mt-1">{(draft.address || "").length}/500</div>
          </div>
          <Row cols={3}>
            <div>
              <Label className={label}>Email</Label>
              <Input data-testid="company-email-input" value={draft.email} onChange={(e) => set("email", e.target.value)} placeholder="info@perusahaan.id" className="rounded-xl" />
            </div>
            <div>
              <Label className={label}>Telepon</Label>
              <Input data-testid="company-phone-input" value={draft.phone} onChange={(e) => set("phone", e.target.value)} placeholder="0812xxxxxxx" className="rounded-xl" />
            </div>
            <div>
              <Label className={label}>Website</Label>
              <Input data-testid="company-web-input" value={draft.web} onChange={(e) => set("web", e.target.value)} placeholder="perusahaan.id" className="rounded-xl" />
            </div>
          </Row>
        </div>
      </div>
      <SaveBar dirty={dirty} busy={busy} onSave={save} onReset={reset} testid="company-identity" />
    </div>
  );
}

/* ---------------- 2. Nomor & Dokumen ---------------- */

function DocumentsSection({ company, onSave }) {
  const keys = ["quote_prefix", "invoice_prefix", "invoice_prefix_non_ppn", "invoice_due_days"];
  const { draft, set, dirty, reset } = useDraft(company, keys);
  const [busy, setBusy] = useState(false);

  const save = async () => {
    setBusy(true);
    try {
      await onSave({
        quote_prefix: draft.quote_prefix || "",
        invoice_prefix: draft.invoice_prefix || "",
        invoice_prefix_non_ppn: draft.invoice_prefix_non_ppn || "",
        invoice_due_days: Number(draft.invoice_due_days) || 0,
      });
    } catch (err) { toast.error(err.response?.data?.detail || "Gagal menyimpan"); }
    finally { setBusy(false); }
  };

  return (
    <div className={cardCls} data-testid="company-documents-section">
      <Row cols={3}>
        <div>
          <Label className={label}>Seri Quotation</Label>
          <Input data-testid="company-quote-prefix-input" value={draft.quote_prefix} onChange={(e) => set("quote_prefix", e.target.value)} placeholder="QTN-IDS" className="rounded-xl font-num" />
          <p className="text-[11px] text-[#A0A3A8] mt-1.5">Contoh: 001/<b>QTN-IDS</b>/NLU/IX/2026</p>
        </div>
        <div>
          <Label className={label}>Seri Invoice PPN</Label>
          <Input data-testid="company-invoice-prefix-input" value={draft.invoice_prefix} onChange={(e) => set("invoice_prefix", e.target.value)} placeholder="INV-IDS" className="rounded-xl font-num" />
          <p className="text-[11px] text-[#A0A3A8] mt-1.5">Dipakai untuk invoice ber-PPN.</p>
        </div>
        <div>
          <Label className={label}>Seri Invoice Non-PPN</Label>
          <Input data-testid="company-invoice-prefix-nonppn-input" value={draft.invoice_prefix_non_ppn} onChange={(e) => set("invoice_prefix_non_ppn", e.target.value)} placeholder="P-INV-IDS" className="rounded-xl font-num" />
          <p className="text-[11px] text-[#A0A3A8] mt-1.5">Dipakai untuk invoice tanpa PPN.</p>
        </div>
      </Row>

      <div className="mt-5">
        <Label className={label}>Jatuh Tempo Default</Label>
        <div className="flex flex-wrap items-center gap-2">
          <div className="relative">
            <Input
              data-testid="company-invoice-due-days-input" type="number" min="0"
              value={draft.invoice_due_days} onChange={(e) => set("invoice_due_days", e.target.value)}
              className="rounded-xl w-28 pr-12 font-num"
            />
            <span className="absolute right-3 top-1/2 -translate-y-1/2 text-[11px] text-[#A0A3A8] font-semibold">hari</span>
          </div>
          {[7, 14, 30, 60].map((d) => (
            <button
              key={d} onClick={() => set("invoice_due_days", d)} data-testid={`company-due-preset-${d}`}
              className={`h-9 px-4 rounded-full text-xs font-bold border transition-colors ${
                Number(draft.invoice_due_days) === d ? "bg-[#3B4A63] text-white border-[#3B4A63]" : "bg-white text-[#626061] border-[#DCE0E7] hover:border-[#3B4A63]"
              }`}
            >
              {d} hari
            </button>
          ))}
        </div>
      </div>
      <SaveBar dirty={dirty} busy={busy} onSave={save} onReset={reset} testid="company-documents" />
    </div>
  );
}

/* ---------------- 3. Rekening Bank ---------------- */

const emptyBank = { label: "", kind: "pt", bank_name: "", account_no: "", holder: "", is_default_ppn: false, is_default_non_ppn: false };

function BankAccountsSection() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialog, setDialog] = useState(null); // {mode, data}
  const [confirm, setConfirm] = useState(null);

  const load = () => api.get("/settings/bank-accounts", { params: { all: 1 } })
    .then((r) => setRows(r.data || [])).catch(() => toast.error("Gagal memuat rekening")).finally(() => setLoading(false));
  useEffect(() => { load(); }, []);

  const setDefault = async (b, type) => {
    try {
      await api.patch(`/settings/bank-accounts/${b.id}`, {
        label: b.label, kind: b.kind, bank_name: b.bank_name, account_no: b.account_no, holder: b.holder,
        is_default_ppn: type === "ppn" ? true : !!b.is_default_ppn,
        is_default_non_ppn: type === "non_ppn" ? true : !!b.is_default_non_ppn,
        is_active: true,
      });
      toast.success(`${b.bank_name} jadi default invoice ${type === "ppn" ? "PPN" : "Non-PPN"}`);
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Gagal"); }
  };

  const remove = async () => {
    const b = confirm;
    setConfirm(null);
    try {
      const r = await api.delete(`/settings/bank-accounts/${b.id}`);
      toast.success(r.data?.deactivated ? "Rekening dipakai invoice lama — dinonaktifkan" : "Rekening dihapus");
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Gagal menghapus"); }
  };

  if (loading) return <div className={cardCls}><Loader2 className="w-4 h-4 animate-spin text-[#A0A3A8]" /></div>;

  return (
    <div className="space-y-3" data-testid="company-banks-section">
      {rows.length === 0 ? (
        <EmptyState
          icon={CreditCard} title="Belum ada rekening"
          hint="Tambahkan rekening PT untuk invoice PPN dan rekening pribadi untuk invoice Non-PPN."
          cta="Tambah Rekening" onCta={() => setDialog({ mode: "create", data: emptyBank })}
          testid="company-banks-empty"
        />
      ) : (
        <>
          <div className="grid grid-cols-1 xl:grid-cols-2 gap-3">
            {rows.map((b) => (
              <div
                key={b.id} data-testid={`bank-card-${b.id}`}
                className={`rounded-2xl border p-4 transition-colors ${b.is_active ? "bg-white border-[#DCE0E7] hover:border-[#3B4A63]" : "bg-[#FAFBFD] border-dashed border-[#DCE0E7] opacity-70"}`}
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-sm font-bold text-[#090808] truncate">{b.bank_name}</span>
                      <Badge tone={b.kind === "pt" ? "blue" : "gray"} testid={`bank-kind-${b.id}`}>{b.kind === "pt" ? "PT" : "Pribadi"}</Badge>
                      {b.is_default_ppn && <Badge tone="green" testid={`bank-default-ppn-${b.id}`}><Star className="w-2.5 h-2.5" /> Default PPN</Badge>}
                      {b.is_default_non_ppn && <Badge tone="purple" testid={`bank-default-nonppn-${b.id}`}><Star className="w-2.5 h-2.5" /> Default Non-PPN</Badge>}
                      {!b.is_active && <Badge tone="gray">Nonaktif</Badge>}
                    </div>
                    <div className="text-[15px] font-bold text-[#090808] font-num tabular-nums mt-1.5">{b.account_no}</div>
                    <div className="text-xs text-[#626061] mt-0.5 truncate">a.n. {b.holder}</div>
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    {b.is_active && !b.is_default_ppn && (
                      <button onClick={() => setDefault(b, "ppn")} data-testid={`bank-set-default-ppn-${b.id}`} title="Jadikan default invoice PPN"
                        className="h-8 px-2.5 rounded-full bg-[#F4F5F7] hover:bg-[#C9F0D6] text-[#3B4A63] text-[10px] font-bold inline-flex items-center gap-1"><Star className="w-3 h-3" /> PPN</button>
                    )}
                    {b.is_active && !b.is_default_non_ppn && (
                      <button onClick={() => setDefault(b, "non_ppn")} data-testid={`bank-set-default-nonppn-${b.id}`} title="Jadikan default invoice Non-PPN"
                        className="h-8 px-2.5 rounded-full bg-[#F4F5F7] hover:bg-[#F3E8FF] text-[#3B4A63] text-[10px] font-bold inline-flex items-center gap-1"><Star className="w-3 h-3" /> Non-PPN</button>
                    )}
                    <button onClick={() => setDialog({ mode: "edit", data: b })} data-testid={`bank-edit-${b.id}`} title="Edit"
                      className="w-8 h-8 rounded-full bg-[#F4F5F7] hover:bg-[#E5EEFA] text-[#3B4A63] inline-flex items-center justify-center"><Pencil className="w-3.5 h-3.5" /></button>
                    <button onClick={() => setConfirm(b)} data-testid={`bank-delete-${b.id}`} title="Hapus"
                      className="w-8 h-8 rounded-full bg-[#F4F5F7] hover:bg-[#FDE7E9] text-[#CE6968] inline-flex items-center justify-center"><Trash2 className="w-3.5 h-3.5" /></button>
                  </div>
                </div>
              </div>
            ))}
          </div>
          <Button onClick={() => setDialog({ mode: "create", data: emptyBank })} data-testid="bank-add-btn"
            className="h-11 rounded-full bg-[#090808] hover:bg-black gap-1.5 px-5">
            <Plus className="w-4 h-4" /> Tambah Rekening
          </Button>
        </>
      )}

      <BankDialog
        open={!!dialog} mode={dialog?.mode} initial={dialog?.data}
        onClose={() => setDialog(null)} onSaved={() => { setDialog(null); load(); }}
      />

      <AlertDialog open={!!confirm} onOpenChange={(v) => !v && setConfirm(null)}>
        <AlertDialogContent className="rounded-3xl" data-testid="bank-delete-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle>Hapus rekening ini?</AlertDialogTitle>
            <AlertDialogDescription>
              {confirm?.bank_name} · {confirm?.account_no}. Bila rekening sudah dipakai invoice, rekening hanya akan dinonaktifkan agar dokumen lama tetap utuh.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="bank-delete-cancel">Batal</AlertDialogCancel>
            <AlertDialogAction onClick={remove} data-testid="bank-delete-confirm-btn" className="bg-[#CE6968] hover:bg-[#b95958]">Hapus</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function BankDialog({ open, mode, initial, onClose, onSaved }) {
  const [f, setF] = useState(emptyBank);
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) setF({ ...emptyBank, ...(initial || {}) }); }, [open, initial]);
  const set = (k, v) => setF((x) => ({ ...x, [k]: v }));

  const save = async () => {
    if (!f.bank_name.trim() || !f.account_no.trim() || !f.holder.trim()) {
      return toast.error("Nama bank, no. rekening, dan atas nama wajib diisi");
    }
    setBusy(true);
    try {
      const payload = {
        label: f.label || f.bank_name, kind: f.kind, bank_name: f.bank_name,
        account_no: f.account_no, holder: f.holder,
        is_default_ppn: !!f.is_default_ppn, is_default_non_ppn: !!f.is_default_non_ppn,
        is_active: f.is_active !== false,
      };
      if (mode === "edit") await api.patch(`/settings/bank-accounts/${f.id}`, payload);
      else await api.post("/settings/bank-accounts", payload);
      toast.success(mode === "edit" ? "Rekening diperbarui" : "Rekening ditambahkan");
      onSaved();
    } catch (err) { toast.error(err.response?.data?.detail || "Gagal menyimpan"); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-lg rounded-3xl border-0 p-6" data-testid="bank-dialog">
        <DialogTitle className="text-lg font-bold text-[#090808]">{mode === "edit" ? "Edit Rekening" : "Tambah Rekening"}</DialogTitle>
        <DialogDescription className="text-xs text-[#A0A3A8]">Rekening ini bisa dipilih pada dropdown saat membuat invoice.</DialogDescription>

        <div className="space-y-4 pt-1">
          <div>
            <Label className={label}>Jenis Rekening</Label>
            <div className="flex items-center gap-2">
              {[["pt", "Rekening PT"], ["personal", "Rekening Pribadi"]].map(([v, l]) => (
                <button key={v} onClick={() => set("kind", v)} data-testid={`bank-kind-${v}`}
                  className={`h-9 px-4 rounded-full text-xs font-bold border transition-colors ${f.kind === v ? "bg-[#3B4A63] text-white border-[#3B4A63]" : "bg-white text-[#626061] border-[#DCE0E7] hover:border-[#3B4A63]"}`}>
                  {l}
                </button>
              ))}
            </div>
          </div>
          <Row>
            <div>
              <Label className={label}>Nama Bank</Label>
              <Input data-testid="bank-name-input" value={f.bank_name} onChange={(e) => set("bank_name", e.target.value)} placeholder="BANK BCA KCP Sudirman" className="rounded-xl" />
            </div>
            <div>
              <Label className={label}>No. Rekening</Label>
              <Input data-testid="bank-account-input" value={f.account_no} onChange={(e) => set("account_no", e.target.value)} placeholder="6530069671" className="rounded-xl font-num tabular-nums" />
            </div>
          </Row>
          <div>
            <Label className={label}>Atas Nama</Label>
            <Input data-testid="bank-holder-input" value={f.holder} onChange={(e) => set("holder", e.target.value)} placeholder="PT IPTEC DIGITAL SOLUTION" className="rounded-xl" />
          </div>
          <div>
            <Label className={label}>Label (opsional)</Label>
            <Input data-testid="bank-label-input" value={f.label} onChange={(e) => set("label", e.target.value)} placeholder="mis. BCA Operasional" className="rounded-xl" />
          </div>
          <div className="rounded-2xl bg-[#FAFBFD] border border-[#DCE0E7] p-4 space-y-2.5">
            <Toggle
              checked={!!f.is_default_ppn} onChange={(v) => set("is_default_ppn", v)} testid="bank-default-ppn-toggle"
              title="Jadikan default invoice PPN" hint="Otomatis terpilih saat membuat invoice ber-PPN"
            />
            <Toggle
              checked={!!f.is_default_non_ppn} onChange={(v) => set("is_default_non_ppn", v)} testid="bank-default-nonppn-toggle"
              title="Jadikan default invoice Non-PPN" hint="Otomatis terpilih saat membuat invoice tanpa PPN"
            />
          </div>
        </div>

        <div className="flex justify-end gap-2 pt-2">
          <button onClick={onClose} data-testid="bank-dialog-cancel" className="h-10 px-4 rounded-full text-sm font-semibold text-[#626061] hover:bg-[#F4F5F7]">Batal</button>
          <Button onClick={save} disabled={busy} data-testid="bank-dialog-save" className="h-10 rounded-full bg-[#090808] hover:bg-black gap-1.5 px-5">
            {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />} Simpan
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function Toggle({ checked, onChange, title, hint, testid }) {
  return (
    <button onClick={() => onChange(!checked)} data-testid={testid} className="w-full flex items-start gap-3 text-left group">
      <span className={`w-5 h-5 mt-0.5 rounded-md border flex items-center justify-center shrink-0 transition-colors ${checked ? "bg-[#090808] border-[#090808] text-white" : "bg-white border-[#DCE0E7] group-hover:border-[#3B4A63]"}`}>
        {checked && <CheckCircle2 className="w-3.5 h-3.5" />}
      </span>
      <span className="min-w-0">
        <span className="block text-[13px] font-semibold text-[#090808]">{title}</span>
        <span className="block text-[11px] text-[#A0A3A8]">{hint}</span>
      </span>
    </button>
  );
}

/* ---------------- 4. Pajak & NPWP ---------------- */

function TaxSection({ company, onSave }) {
  const keys = [
    "npwp_ppn_name", "npwp_ppn_number", "npwp_ppn_address",
    "npwp_nonppn_name", "npwp_nonppn_number", "npwp_nonppn_nik", "npwp_nonppn_address",
  ];
  const { draft, set, dirty, reset } = useDraft(company, keys);
  const [busy, setBusy] = useState(false);

  const save = async () => {
    setBusy(true);
    try { await onSave(keys.reduce((a, k) => ({ ...a, [k]: draft[k] || "" }), {})); }
    catch (err) { toast.error(err.response?.data?.detail || "Gagal menyimpan"); }
    finally { setBusy(false); }
  };

  return (
    <div className={cardCls} data-testid="company-tax-section">
      <div className="space-y-6">
        <div>
          <div className="flex items-center gap-2 mb-3">
            <Badge tone="blue">Invoice PPN</Badge>
            <span className="text-[13px] font-bold text-[#090808]">Data NPWP Perusahaan</span>
          </div>
          <Row>
            <div>
              <Label className={label}>Nama Perusahaan (NPWP)</Label>
              <Input data-testid="company-npwp-ppn-name-input" value={draft.npwp_ppn_name} onChange={(e) => set("npwp_ppn_name", e.target.value)} className="rounded-xl" />
            </div>
            <div>
              <Label className={label}>Nomor NPWP</Label>
              <Input data-testid="company-npwp-ppn-number-input" value={draft.npwp_ppn_number} onChange={(e) => set("npwp_ppn_number", e.target.value)} className="rounded-xl font-num" />
            </div>
          </Row>
          <div className="mt-4">
            <Label className={label}>Alamat NPWP</Label>
            <textarea
              data-testid="company-npwp-ppn-address-input" rows={2} value={draft.npwp_ppn_address} onChange={(e) => set("npwp_ppn_address", e.target.value)}
              className="w-full rounded-xl border border-[#DCE0E7] bg-white px-3 py-2 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-black/10"
            />
          </div>
        </div>

        <div className="pt-5 border-t border-[#DCE0E7]">
          <div className="flex items-center gap-2 mb-3">
            <Badge tone="gray">Invoice Non-PPN</Badge>
            <span className="text-[13px] font-bold text-[#090808]">Data Pribadi / Penanggung Jawab</span>
          </div>
          <Row cols={3}>
            <div>
              <Label className={label}>Nama</Label>
              <Input data-testid="company-npwp-nonppn-name-input" value={draft.npwp_nonppn_name} onChange={(e) => set("npwp_nonppn_name", e.target.value)} className="rounded-xl" />
            </div>
            <div>
              <Label className={label}>Nomor NPWP</Label>
              <Input data-testid="company-npwp-nonppn-number-input" value={draft.npwp_nonppn_number} onChange={(e) => set("npwp_nonppn_number", e.target.value)} className="rounded-xl font-num" />
            </div>
            <div>
              <Label className={label}>NIK</Label>
              <Input data-testid="company-npwp-nonppn-nik-input" value={draft.npwp_nonppn_nik} onChange={(e) => set("npwp_nonppn_nik", e.target.value)} className="rounded-xl font-num" />
            </div>
          </Row>
          <div className="mt-4">
            <Label className={label}>Alamat</Label>
            <textarea
              data-testid="company-npwp-nonppn-address-input" rows={2} value={draft.npwp_nonppn_address} onChange={(e) => set("npwp_nonppn_address", e.target.value)}
              className="w-full rounded-xl border border-[#DCE0E7] bg-white px-3 py-2 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-black/10"
            />
          </div>
        </div>
      </div>
      <SaveBar dirty={dirty} busy={busy} onSave={save} onReset={reset} testid="company-tax" />
    </div>
  );
}

/* ---------------- 5. Penandatangan ---------------- */

function SignersSection() {
  const [rows, setRows] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialog, setDialog] = useState(null);
  const [confirm, setConfirm] = useState(null);

  const load = () => api.get("/settings/invoice-signers", { params: { all: 1 } })
    .then((r) => setRows(r.data || [])).catch(() => toast.error("Gagal memuat penandatangan")).finally(() => setLoading(false));
  useEffect(() => {
    load();
    api.get("/users").then((r) => setUsers(r.data || [])).catch(() => {});
  }, []);

  const setDefault = async (s) => {
    try {
      await api.patch(`/settings/invoice-signers/${s.id}`, { is_default: true });
      toast.success(`${s.name} jadi penandatangan default`);
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Gagal"); }
  };

  const remove = async () => {
    const s = confirm;
    setConfirm(null);
    try {
      const r = await api.delete(`/settings/invoice-signers/${s.id}`);
      toast.success(r.data?.deactivated ? "Sudah dipakai invoice lama — dinonaktifkan" : "Penandatangan dihapus");
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Gagal menghapus"); }
  };

  if (loading) return <div className={cardCls}><Loader2 className="w-4 h-4 animate-spin text-[#A0A3A8]" /></div>;

  return (
    <div className="space-y-3" data-testid="company-signers-section">
      {rows.length === 0 ? (
        <EmptyState
          icon={PenTool} title="Belum ada penandatangan"
          hint="Tambahkan user yang berwenang menandatangani invoice — umumnya CEO atau Finance Director."
          cta="Tambah Penandatangan" onCta={() => setDialog({ mode: "create" })}
          testid="company-signers-empty"
        />
      ) : (
        <>
          <div className="space-y-2.5">
            {rows.map((s) => (
              <div key={s.id} data-testid={`signer-card-${s.id}`}
                className={`rounded-2xl border p-4 flex items-center gap-3 transition-colors ${s.is_active ? "bg-white border-[#DCE0E7] hover:border-[#3B4A63]" : "bg-[#FAFBFD] border-dashed border-[#DCE0E7] opacity-70"}`}>
                <Avatar name={s.name} picture={s.picture} size="md" />
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-sm font-bold text-[#090808] truncate">{s.name}</span>
                    {s.is_default && <Badge tone="green" testid={`signer-default-${s.id}`}><Star className="w-2.5 h-2.5" /> Default</Badge>}
                    {!s.is_active && <Badge tone="gray">Nonaktif</Badge>}
                  </div>
                  <div className="text-xs text-[#626061] mt-0.5 truncate">{s.title || "—"}{s.email ? ` · ${s.email}` : ""}</div>
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  {!s.is_default && s.is_active && (
                    <button onClick={() => setDefault(s)} data-testid={`signer-set-default-${s.id}`} title="Jadikan default"
                      className="w-8 h-8 rounded-full bg-[#F4F5F7] hover:bg-[#C9F0D6] text-[#3B4A63] inline-flex items-center justify-center"><Star className="w-3.5 h-3.5" /></button>
                  )}
                  <button onClick={() => setDialog({ mode: "edit", data: s })} data-testid={`signer-edit-${s.id}`} title="Edit jabatan"
                    className="w-8 h-8 rounded-full bg-[#F4F5F7] hover:bg-[#E5EEFA] text-[#3B4A63] inline-flex items-center justify-center"><Pencil className="w-3.5 h-3.5" /></button>
                  <button onClick={() => setConfirm(s)} data-testid={`signer-delete-${s.id}`} title="Hapus"
                    className="w-8 h-8 rounded-full bg-[#F4F5F7] hover:bg-[#FDE7E9] text-[#CE6968] inline-flex items-center justify-center"><Trash2 className="w-3.5 h-3.5" /></button>
                </div>
              </div>
            ))}
          </div>
          <Button onClick={() => setDialog({ mode: "create" })} data-testid="signer-add-btn" className="h-11 rounded-full bg-[#090808] hover:bg-black gap-1.5 px-5">
            <Plus className="w-4 h-4" /> Tambah Penandatangan
          </Button>
        </>
      )}

      <SignerDialog
        open={!!dialog} mode={dialog?.mode} initial={dialog?.data}
        users={users} existing={rows}
        onClose={() => setDialog(null)} onSaved={() => { setDialog(null); load(); }}
      />

      <AlertDialog open={!!confirm} onOpenChange={(v) => !v && setConfirm(null)}>
        <AlertDialogContent className="rounded-3xl" data-testid="signer-delete-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle>Hapus penandatangan ini?</AlertDialogTitle>
            <AlertDialogDescription>
              {confirm?.name} tidak lagi muncul sebagai pilihan penandatangan. Bila sudah dipakai invoice, datanya hanya dinonaktifkan.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="signer-delete-cancel">Batal</AlertDialogCancel>
            <AlertDialogAction onClick={remove} data-testid="signer-delete-confirm-btn" className="bg-[#CE6968] hover:bg-[#b95958]">Hapus</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function SignerDialog({ open, mode, initial, users, existing, onClose, onSaved }) {
  const [userId, setUserId] = useState("");
  const [title, setTitle] = useState("");
  const [isDefault, setIsDefault] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    setUserId(initial?.user_id || "");
    setTitle(initial?.title || "");
    setIsDefault(!!initial?.is_default);
  }, [open, initial]);

  const taken = new Set((existing || []).map((s) => s.user_id));
  const options = (users || []).filter((u) => u.is_active !== false && (mode === "edit" ? true : !taken.has(u.user_id)));

  const save = async () => {
    if (mode !== "edit" && !userId) return toast.error("Pilih user terlebih dahulu");
    setBusy(true);
    try {
      if (mode === "edit") await api.patch(`/settings/invoice-signers/${initial.id}`, { title, is_default: isDefault });
      else await api.post("/settings/invoice-signers", { user_id: userId, title, is_default: isDefault });
      toast.success(mode === "edit" ? "Penandatangan diperbarui" : "Penandatangan ditambahkan");
      onSaved();
    } catch (err) { toast.error(err.response?.data?.detail || "Gagal menyimpan"); }
    finally { setBusy(false); }
  };

  const picked = (users || []).find((u) => u.user_id === userId);

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-lg rounded-3xl border-0 p-6" data-testid="signer-dialog">
        <DialogTitle className="text-lg font-bold text-[#090808]">{mode === "edit" ? "Edit Penandatangan" : "Tambah Penandatangan"}</DialogTitle>
        <DialogDescription className="text-xs text-[#A0A3A8]">Nama & jabatan ini yang tercetak di atas kotak materai pada PDF invoice.</DialogDescription>

        <div className="space-y-4 pt-1">
          <div>
            <Label className={label}>User</Label>
            {mode === "edit" ? (
              <div className="flex items-center gap-2.5 rounded-xl border border-[#DCE0E7] bg-[#FAFBFD] p-3">
                <Avatar name={initial?.name} picture={initial?.picture} size="sm" />
                <div className="min-w-0">
                  <div className="text-sm font-bold text-[#090808] truncate">{initial?.name}</div>
                  <div className="text-[11px] text-[#A0A3A8] truncate">{initial?.email}</div>
                </div>
              </div>
            ) : (
              <Select value={userId} onValueChange={setUserId}>
                <SelectTrigger data-testid="signer-user-select" className="h-10 rounded-xl bg-white border-[#DCE0E7] text-sm">
                  <SelectValue placeholder="Pilih user" />
                </SelectTrigger>
                <SelectContent>
                  {options.map((u) => (
                    <SelectItem key={u.user_id} value={u.user_id} className="text-sm" data-testid={`signer-user-option-${u.user_id}`}>
                      {u.name} <span className="text-[#A0A3A8]">· {u.job_title || u.role}</span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </div>
          <div>
            <Label className={label}>Jabatan (tercetak di invoice)</Label>
            <Input
              data-testid="signer-title-input" value={title} onChange={(e) => setTitle(e.target.value)}
              placeholder={picked?.job_title || "mis. Chief Executive Officer"} className="rounded-xl"
            />
            <p className="text-[11px] text-[#A0A3A8] mt-1.5">Kosongkan untuk memakai jabatan dari profil user.</p>
          </div>
          <div className="rounded-2xl bg-[#FAFBFD] border border-[#DCE0E7] p-4">
            <Toggle
              checked={isDefault} onChange={setIsDefault} testid="signer-default-toggle"
              title="Jadikan penandatangan default" hint="Otomatis terpilih pada invoice baru"
            />
          </div>
        </div>

        <div className="flex justify-end gap-2 pt-2">
          <button onClick={onClose} data-testid="signer-dialog-cancel" className="h-10 px-4 rounded-full text-sm font-semibold text-[#626061] hover:bg-[#F4F5F7]">Batal</button>
          <Button onClick={save} disabled={busy} data-testid="signer-dialog-save" className="h-10 rounded-full bg-[#090808] hover:bg-black gap-1.5 px-5">
            {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />} Simpan
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
